import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  LinearProgress,
  TextField,
  Switch,
  FormControlLabel,
  Tooltip,
  Autocomplete,
  Select,
  FormControl,
  InputLabel,
  Collapse,
} from '@mui/material';
import {
  Edit as EditIcon,
  Publish as ReleaseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Check as CheckIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PlayArrow as PlayArrowIcon,
  ExpandMore as ExpandMoreIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import api from '../api/axios';
import ChainOfThought from '../components/ChainOfThought';

import type { Entity, EntityType, ReasoningEffort } from '../types/prompts';

interface PromptVersion {
  id: string;
  entity_type: string;
  entity_type_slug: string;
  entity_type_name: string;
  version: string;
  status: 'draft' | 'released' | 'archived';
  is_active: boolean;
  notes: string;
  created_at: string;
}

interface PromptVersionsResponse {
  count: number;
  results: PromptVersion[];
}

interface EntitiesResponse {
  count?: number;
  results?: Entity[];
}

interface EntityFormData {
  entity_type_slug: string;
  name: string;
  definition: string;
  metadata: string; // JSON string for json format
  is_active: boolean;
}

interface Props {
  entityType: EntityType;
  allEntityTypes: EntityType[];
  onEntityTypeUpdated: () => void;
  onEntityTypeDeleted: () => void;
}

const ACTIVE_TEST_RUN_KEY = 'activeTestRun';

export default function EntityTypeDetail({ entityType, allEntityTypes, onEntityTypeUpdated, onEntityTypeDeleted }: Props) {
  // Settings form state
  const [settingsLabel, setSettingsLabel] = useState(entityType.label);
  const [settingsShortName, setSettingsShortName] = useState(entityType.short_name);
  const [settingsDescription, setSettingsDescription] = useState(entityType.description);
  const [settingsDeps, setSettingsDeps] = useState<string[]>(entityType.depends_on || []);
  const [settingsEntityFormat, setSettingsEntityFormat] = useState(entityType.entity_format);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSuccess, setSettingsSuccess] = useState(false);

  // Delete entity type state
  const [deleteTypeDialogOpen, setDeleteTypeDialogOpen] = useState(false);
  const [deletingType, setDeletingType] = useState(false);

  // Reset settings when entity type changes
  useEffect(() => {
    setSettingsLabel(entityType.label);
    setSettingsShortName(entityType.short_name);
    setSettingsDescription(entityType.description);
    setSettingsDeps(entityType.depends_on || []);
    setSettingsEntityFormat(entityType.entity_format);
  }, [entityType.id]);

  // Prompt versions state
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedVersion, setSelectedVersion] = useState<PromptVersion | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteVersion, setDeleteVersion] = useState<PromptVersion | null>(null);
  const [feedbackCount, setFeedbackCount] = useState<number>(0);
  const [deleting, setDeleting] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [releaseVersion, setReleaseVersion] = useState<PromptVersion | null>(null);
  const [forceOverwrite, setForceOverwrite] = useState(false);
  const [releasing, setReleasing] = useState(false);

  // Entity (known answers) state
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [entityError, setEntityError] = useState<string | null>(null);
  const [entityDialogOpen, setEntityDialogOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState<Entity | null>(null);
  const [entityFormData, setEntityFormData] = useState<EntityFormData>({
    entity_type_slug: '',
    name: '',
    definition: '',
    metadata: '{}',
    is_active: true,
  });
  const [savingEntity, setSavingEntity] = useState(false);
  const [deleteEntityDialogOpen, setDeleteEntityDialogOpen] = useState(false);
  const [deleteEntity, setDeleteEntity] = useState<Entity | null>(null);
  const [deletingEntity, setDeletingEntity] = useState(false);

  // Prompt editor modal state
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [promptModalMode, setPromptModalMode] = useState<'create' | 'edit'>('create');
  const [promptModalVersionId, setPromptModalVersionId] = useState<string | null>(null);
  const [promptText, setPromptText] = useState('');
  const [reasoningEffort, setReasoningEffort] = useState<ReasoningEffort>('medium');
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [versionNotes, setVersionNotes] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [promptError, setPromptError] = useState<string | null>(null);
  const [loadingPromptText, setLoadingPromptText] = useState(false);
  const promptTextRef = useRef<HTMLTextAreaElement>(null);

  // Inline test runner state (inside prompt editor modal)
  const [evalPressReleases, setEvalPressReleases] = useState<{ press_release_id: string; case_name: string }[]>([]);
  const [selectedTestPrId, setSelectedTestPrId] = useState<string>('');
  const [inlineTestRunId, setInlineTestRunId] = useState<string | null>(null);
  const [inlineTestStatus, setInlineTestStatus] = useState<'idle' | 'running' | 'completed' | 'failed'>('idle');
  const [inlineTestResult, setInlineTestResult] = useState<{ output: any; latency_ms: number; tokens_used: number; metadata: any } | null>(null);
  const [inlineTestError, setInlineTestError] = useState<string | null>(null);
  const inlineTestPollRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedPrDetail, setSelectedPrDetail] = useState<{ title: string; body: string } | null>(null);
  const [loadingPrDetail, setLoadingPrDetail] = useState(false);
  const [prPreviewOpen, setPrPreviewOpen] = useState(false);

  // Test run progress notification state
  const [activeTestRun, setActiveTestRun] = useState<{
    testRunId: string;
    versionId: string;
    version: string;
    entityType: string;
    entityTypeLabel: string;
    status: 'pending' | 'running' | 'completed' | 'failed';
    completedPressReleases: number;
    totalPressReleases: number;
  } | null>(() => {
    try {
      const saved = localStorage.getItem(ACTIVE_TEST_RUN_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.status === 'pending' || parsed.status === 'running') {
          return parsed;
        }
      }
    } catch {
      // Ignore parse errors
    }
    return null;
  });

  // Persist activeTestRun to localStorage
  useEffect(() => {
    if (activeTestRun) {
      localStorage.setItem(ACTIVE_TEST_RUN_KEY, JSON.stringify(activeTestRun));
    } else {
      localStorage.removeItem(ACTIVE_TEST_RUN_KEY);
    }
  }, [activeTestRun]);

  // Poll test run status when active
  useEffect(() => {
    if (!activeTestRun || activeTestRun.status === 'completed' || activeTestRun.status === 'failed') {
      if (activeTestRun?.status === 'completed' || activeTestRun?.status === 'failed') {
        localStorage.removeItem(ACTIVE_TEST_RUN_KEY);
      }
      return;
    }

    if (!activeTestRun.testRunId) {
      setActiveTestRun(null);
      localStorage.removeItem(ACTIVE_TEST_RUN_KEY);
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await api.get(`/schemes/prompts/test-runs/${activeTestRun.testRunId}/`);
        const data = response.data;

        setActiveTestRun(prev => prev ? {
          ...prev,
          status: data.status,
          completedPressReleases: data.completed_press_releases,
          totalPressReleases: data.total_press_releases,
        } : null);

        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollInterval);
          localStorage.removeItem(ACTIVE_TEST_RUN_KEY);
          loadVersions(entityType.slug);
          if (data.status === 'completed') {
            setTimeout(() => setActiveTestRun(null), 5000);
          }
        }
      } catch (err) {
        console.error('Failed to poll test run status:', err);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [activeTestRun?.testRunId, activeTestRun?.status]);

  // Load prompt versions when entity type changes
  const loadVersions = (slug: string) => {
    setLoadingVersions(true);
    api.get<PromptVersionsResponse>('/schemes/prompts/versions/', {
      params: { entity_type: slug }
    })
      .then((res) => {
        const results = res.data.results || [];
        results.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        setVersions(results);
      })
      .catch((err) => console.error('Failed to load prompt versions:', err))
      .finally(() => setLoadingVersions(false));
  };

  useEffect(() => {
    loadVersions(entityType.slug);
  }, [entityType.slug]);

  // Load entities when entity type changes
  useEffect(() => {
    setLoadingEntities(true);
    api.get<EntitiesResponse>('/schemes/prompts/entities/', {
      params: { entity_type: entityType.slug, active_only: 'false' }
    })
      .then((res) => {
        const results = res.data.results || (res.data as unknown as Entity[]);
        setEntities(Array.isArray(results) ? results : []);
      })
      .catch((err) => {
        console.error('Failed to load entities:', err);
        setEntityError('Failed to load entities');
      })
      .finally(() => setLoadingEntities(false));
  }, [entityType.slug]);

  // --- Settings save ---
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    setSettingsSuccess(false);
    try {
      await api.patch(`/schemes/prompts/entity-types/${entityType.id}/`, {
        label: settingsLabel,
        short_name: settingsShortName,
        description: settingsDescription,
        entity_format: settingsEntityFormat,
        depends_on_slugs: settingsDeps,
      });
      setSettingsSuccess(true);
      onEntityTypeUpdated();
      setTimeout(() => setSettingsSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  // --- Toggle visibility ---
  const handleToggleVisibility = async () => {
    try {
      await api.post(`/schemes/prompts/entity-types/${entityType.id}/toggle-visibility/`);
      onEntityTypeUpdated();
    } catch (err) {
      console.error('Failed to toggle visibility:', err);
    }
  };

  // --- Delete entity type ---
  const handleDeleteEntityType = async () => {
    setDeletingType(true);
    try {
      await api.delete(`/schemes/prompts/entity-types/${entityType.id}/`);
      setDeleteTypeDialogOpen(false);
      onEntityTypeDeleted();
    } catch (err) {
      console.error('Failed to delete entity type:', err);
    } finally {
      setDeletingType(false);
    }
  };

  // --- Prompt version actions ---
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, version: PromptVersion) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedVersion(version);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedVersion(null);
  };

  const handleEdit = () => {
    if (selectedVersion) {
      handleOpenPromptModal('edit', selectedVersion.id);
    }
    handleMenuClose();
  };

  const handleReleaseClick = () => {
    if (!selectedVersion) return;
    setReleaseVersion(selectedVersion);
    setForceOverwrite(false);
    handleMenuClose();
    setReleaseDialogOpen(true);
  };

  const handleReleaseCancel = () => {
    setReleaseDialogOpen(false);
    setReleaseVersion(null);
    setForceOverwrite(false);
  };

  const handleReleaseConfirm = async () => {
    if (!releaseVersion) return;
    setReleasing(true);

    try {
      await api.post(`/schemes/prompts/versions/${releaseVersion.id}/release/`);
      const testResponse = await api.post(`/schemes/prompts/versions/${releaseVersion.id}/test/`, {
        release_mode: true,
        force_overwrite: forceOverwrite,
      });

      setActiveTestRun({
        testRunId: testResponse.data.id,
        versionId: releaseVersion.id,
        version: releaseVersion.version,
        entityType: releaseVersion.entity_type_slug,
        entityTypeLabel: releaseVersion.entity_type_name,
        status: testResponse.data.status,
        completedPressReleases: testResponse.data.completed_press_releases || 0,
        totalPressReleases: testResponse.data.total_press_releases,
      });

      loadVersions(entityType.slug);
      setReleaseDialogOpen(false);
      setReleaseVersion(null);
      setForceOverwrite(false);
    } catch (err) {
      console.error('Failed to release:', err);
    } finally {
      setReleasing(false);
    }
  };

  const handleDeleteClick = async () => {
    if (!selectedVersion) return;
    handleMenuClose();

    try {
      const res = await api.get<{ feedback: { press_release_id: string }[] }>(
        `/schemes/prompts/versions/${selectedVersion.id}/feedback/`
      );
      setFeedbackCount(res.data.feedback?.length || 0);
    } catch (err) {
      setFeedbackCount(0);
    }

    setDeleteVersion(selectedVersion);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteVersion) return;
    setDeleting(true);

    try {
      await api.delete(`/schemes/prompts/versions/${deleteVersion.id}/`);
      setVersions(prev => prev.filter(v => v.id !== deleteVersion.id));
      setDeleteDialogOpen(false);
      setDeleteVersion(null);
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteVersion(null);
  };

  const getStatusColor = (status: string, isActive: boolean): 'success' | 'warning' | 'default' => {
    if (isActive) return 'success';
    if (status === 'draft') return 'warning';
    return 'default';
  };

  const getStatusLabel = (status: string, isActive: boolean): string => {
    if (isActive) return 'Active';
    if (status === 'draft') return 'Draft';
    if (status === 'archived') return 'Archived';
    return status;
  };

  // --- Entity (known answers) actions ---
  const handleOpenEntityDialog = (entity?: Entity) => {
    if (entity) {
      setEditingEntity(entity);
      setEntityFormData({
        entity_type_slug: entity.entity_type_slug,
        name: entity.name,
        definition: entity.definition || '',
        metadata: JSON.stringify(entity.metadata || {}, null, 2),
        is_active: entity.is_active,
      });
    } else {
      setEditingEntity(null);
      setEntityFormData({
        entity_type_slug: entityType.slug,
        name: '',
        definition: '',
        metadata: '{}',
        is_active: true,
      });
    }
    setEntityDialogOpen(true);
  };

  const handleCloseEntityDialog = () => {
    setEntityDialogOpen(false);
    setEditingEntity(null);
    setEntityFormData({
      entity_type_slug: entityType.slug,
      name: '',
      definition: '',
      metadata: '{}',
      is_active: true,
    });
  };

  const handleSaveEntity = async () => {
    const isText = entityType.entity_format === 'text';
    const isJson = entityType.entity_format === 'json';

    // Validate content
    if (isText && !entityFormData.definition.trim()) {
      setEntityError('Content is required');
      return;
    }
    if (isJson) {
      try {
        JSON.parse(entityFormData.metadata);
      } catch {
        setEntityError('Invalid JSON');
        return;
      }
    }

    setSavingEntity(true);
    setEntityError(null);

    try {
      const payload: Record<string, unknown> = {
        is_active: entityFormData.is_active,
      };

      if (isText) {
        payload.definition = entityFormData.definition;
        // Auto-generate name from content
        payload.name = entityFormData.definition.split('\n')[0].slice(0, 80).trim() || 'Untitled';
      } else if (isJson) {
        payload.metadata = JSON.parse(entityFormData.metadata);
        payload.name = entityFormData.name.trim() || 'Untitled';
      }

      if (editingEntity) {
        await api.patch(`/schemes/prompts/entities/${editingEntity.id}/`, payload);
      } else {
        await api.post('/schemes/prompts/entities/', {
          entity_type_slug: entityFormData.entity_type_slug,
          ...payload,
        });
      }

      const res = await api.get<EntitiesResponse>('/schemes/prompts/entities/', {
        params: { entity_type: entityType.slug, active_only: 'false' }
      });
      const results = res.data.results || (res.data as unknown as Entity[]);
      setEntities(Array.isArray(results) ? results : []);

      handleCloseEntityDialog();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setEntityError(error.response?.data?.detail || 'Failed to save entity');
    } finally {
      setSavingEntity(false);
    }
  };

  const handleDeleteEntityClick = (entity: Entity) => {
    setDeleteEntity(entity);
    setDeleteEntityDialogOpen(true);
  };

  const handleDeleteEntityConfirm = async () => {
    if (!deleteEntity) return;
    setDeletingEntity(true);
    try {
      await api.delete(`/schemes/prompts/entities/${deleteEntity.id}/`);
      setEntities(prev => prev.filter(e => e.id !== deleteEntity.id));
      setDeleteEntityDialogOpen(false);
      setDeleteEntity(null);
    } catch (err) {
      console.error('Failed to delete entity:', err);
      setEntityError('Failed to delete entity');
    } finally {
      setDeletingEntity(false);
    }
  };

  // --- Prompt editor modal actions ---
  const handleOpenPromptModal = async (mode: 'create' | 'edit', versionId?: string) => {
    setPromptModalMode(mode);
    setPromptError(null);

    if (mode === 'edit' && versionId) {
      // Load existing version data
      setPromptModalVersionId(versionId);
      setLoadingPromptText(true);
      setPromptModalOpen(true);
      try {
        const res = await api.get<import('../types/prompts').PromptVersion>(
          `/schemes/prompts/versions/${versionId}/`
        );
        setPromptText(res.data.prompt_text || '');
        setReasoningEffort(res.data.reasoning_effort || 'medium');
        setWebSearchEnabled(res.data.web_search_enabled ?? false);
        setVersionNotes(res.data.notes || '');
      } catch (err) {
        console.error('Failed to load version:', err);
        setPromptError('Failed to load version data');
      } finally {
        setLoadingPromptText(false);
      }
    } else {
      // Create mode: load active version's prompt as template
      setPromptModalVersionId(null);
      setVersionNotes('');
      setReasoningEffort('medium');
      setWebSearchEnabled(false);

      const activeVersion = versions.find(v => v.is_active);
      if (activeVersion) {
        setLoadingPromptText(true);
        setPromptModalOpen(true);
        try {
          const res = await api.get<import('../types/prompts').PromptVersion>(
            `/schemes/prompts/versions/${activeVersion.id}/`
          );
          setPromptText(res.data.prompt_text || '');
          setReasoningEffort(res.data.reasoning_effort || 'medium');
          setWebSearchEnabled(res.data.web_search_enabled ?? false);
        } catch (err) {
          console.error('Failed to load active version:', err);
          setPromptText('');
        } finally {
          setLoadingPromptText(false);
        }
      } else {
        setPromptText(buildDefaultPromptTemplate());
        setPromptModalOpen(true);
      }
    }
  };

  const buildDefaultPromptTemplate = () => {
    const lines: string[] = [];
    lines.push(`TASK: Analyze the press release below and extract ${entityType.short_name || entityType.label}.`);
    lines.push('');
    if (dependencyVars.length > 0) {
      for (const dep of dependencyVars) {
        const depType = allEntityTypes.find(et => et.slug === dep);
        const label = depType?.short_name || depType?.label || dep;
        lines.push(`${label}:`);
        lines.push(`{${dep}}`);
        lines.push('');
      }
    }
    lines.push('PRESS RELEASE:');
    lines.push('{title}');
    lines.push('');
    lines.push('{body_text}');
    return lines.join('\n');
  };

  const handleClosePromptModal = () => {
    setPromptModalOpen(false);
    setPromptModalVersionId(null);
    setPromptText('');
    setReasoningEffort('medium');
    setWebSearchEnabled(false);
    setVersionNotes('');
    setPromptError(null);
    // Reset inline test state
    setInlineTestRunId(null);
    setInlineTestStatus('idle');
    setInlineTestResult(null);
    setInlineTestError(null);
    setSelectedPrDetail(null);
    setPrPreviewOpen(false);
    if (inlineTestPollRef.current) {
      clearInterval(inlineTestPollRef.current);
      inlineTestPollRef.current = null;
    }
  };

  // Fetch eval press releases when prompt modal opens
  useEffect(() => {
    if (promptModalOpen && evalPressReleases.length === 0) {
      api.get('/schemes/evaluate/')
        .then((res) => {
          const prs = res.data.results || [];
          setEvalPressReleases(prs);
          if (prs.length > 0 && !selectedTestPrId) {
            setSelectedTestPrId(prs[0].press_release_id);
          }
        })
        .catch((err) => console.error('Failed to load eval press releases:', err));
    }
  }, [promptModalOpen]);

  // Fetch press release detail when selection changes
  useEffect(() => {
    if (!selectedTestPrId) {
      setSelectedPrDetail(null);
      return;
    }
    setLoadingPrDetail(true);
    api.get(`/schemes/evaluate/${selectedTestPrId}/`, {
      params: { entity_type: entityType.slug }
    })
      .then((res) => {
        setSelectedPrDetail({
          title: res.data.press_release_title || '',
          body: res.data.press_release_body || '',
        });
      })
      .catch((err) => {
        console.error('Failed to load press release detail:', err);
        setSelectedPrDetail(null);
      })
      .finally(() => setLoadingPrDetail(false));
  }, [selectedTestPrId]);

  // Cleanup inline test polling on unmount
  useEffect(() => {
    return () => {
      if (inlineTestPollRef.current) {
        clearInterval(inlineTestPollRef.current);
      }
    };
  }, []);

  const handleInlineTest = async () => {
    if (!selectedTestPrId || !promptText.trim()) return;

    setInlineTestStatus('running');
    setInlineTestResult(null);
    setInlineTestError(null);

    try {
      const response = await api.post('/schemes/evaluate/test-prompt/', {
        prompt_text: promptText,
        press_release_id: selectedTestPrId,
        entity_type: entityType.slug,
        model: 'gpt-5.2',
        reasoning_effort: reasoningEffort,
        web_search_enabled: webSearchEnabled,
      });

      const testRunId = response.data.test_run_id;
      setInlineTestRunId(testRunId);

      // Poll for results
      inlineTestPollRef.current = setInterval(async () => {
        try {
          const statusRes = await api.get(`/schemes/evaluate/test-prompt/${testRunId}/`);
          const data = statusRes.data;

          if (data.status === 'completed' || data.status === 'failed') {
            if (inlineTestPollRef.current) {
              clearInterval(inlineTestPollRef.current);
              inlineTestPollRef.current = null;
            }

            if (data.status === 'completed' && data.extraction) {
              setInlineTestResult({
                output: data.extraction.output,
                latency_ms: data.extraction.latency_ms,
                tokens_used: data.extraction.tokens_used,
                metadata: data.extraction.metadata,
              });
              setInlineTestStatus('completed');
            } else if (data.status === 'failed') {
              setInlineTestError(data.extraction?.error_message || 'Test failed');
              setInlineTestStatus('failed');
            }
          }
        } catch (pollErr) {
          console.error('Inline test polling error:', pollErr);
        }
      }, 2000);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      setInlineTestError(error.response?.data?.error || error.message || 'Failed to start test');
      setInlineTestStatus('failed');
    }
  };

  const handleInsertVariable = (variable: string) => {
    const textarea = promptTextRef.current;
    if (!textarea) {
      setPromptText(prev => prev + `{${variable}}`);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = promptText;
    const insertion = `{${variable}}`;
    setPromptText(text.slice(0, start) + insertion + text.slice(end));
    // Restore cursor position after insertion
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + insertion.length, start + insertion.length);
    });
  };

  const handleSavePrompt = async () => {
    if (!promptText.trim()) {
      setPromptError('Prompt text is required');
      return;
    }
    setSavingPrompt(true);
    setPromptError(null);

    try {
      if (promptModalMode === 'edit' && promptModalVersionId) {
        await api.patch(`/schemes/prompts/versions/${promptModalVersionId}/`, {
          prompt_text: promptText,
          reasoning_effort: reasoningEffort,
          web_search_enabled: webSearchEnabled,
          notes: versionNotes,
        });
      } else {
        await api.post('/schemes/prompts/versions/', {
          entity_type_slug: entityType.slug,
          prompt_text: promptText,
          reasoning_effort: reasoningEffort,
          web_search_enabled: webSearchEnabled,
          notes: versionNotes,
        });
      }
      handleClosePromptModal();
      loadVersions(entityType.slug);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setPromptError(error.response?.data?.detail || 'Failed to save prompt version');
    } finally {
      setSavingPrompt(false);
    }
  };

  // Available variables for prompt template
  const dependencyVars = (entityType.depends_on || []);
  const builtinVars = ['title', 'body_text', 'known_entities'];

  // Dep options: all other entity types in this account
  const depOptions = allEntityTypes
    .filter(et => et.slug !== entityType.slug && et.is_visible)
    .map(et => et.slug);

  return (
    <>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {entityType.short_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontFamily="monospace">
            {entityType.slug}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={entityType.is_visible ? 'Visible — click to hide' : 'Hidden — click to show'}>
            <IconButton size="small" onClick={handleToggleVisibility}>
              {entityType.is_visible ? (
                <VisibilityIcon sx={{ fontSize: 20 }} />
              ) : (
                <VisibilityOffIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete entity type">
            <IconButton size="small" color="error" onClick={() => setDeleteTypeDialogOpen(true)}>
              <DeleteIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Settings Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Settings</Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={settingsSuccess ? <CheckIcon /> : <SaveIcon />}
            onClick={handleSaveSettings}
            disabled={savingSettings}
            color={settingsSuccess ? 'success' : 'primary'}
          >
            {savingSettings ? 'Saving...' : settingsSuccess ? 'Saved' : 'Save'}
          </Button>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField
            size="small"
            label="Label"
            value={settingsLabel}
            onChange={e => setSettingsLabel(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Short Name"
            value={settingsShortName}
            onChange={e => setSettingsShortName(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Description"
            value={settingsDescription}
            onChange={e => setSettingsDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ gridColumn: '1 / -1' }}
          />
          <Autocomplete
            multiple
            size="small"
            options={depOptions}
            value={settingsDeps}
            onChange={(_, newValue) => {
              setSettingsDeps(newValue);
              // Auto-save depends_on immediately
              api.patch(`/schemes/prompts/entity-types/${entityType.id}/`, {
                depends_on_slugs: newValue,
              }).then(() => onEntityTypeUpdated())
                .catch(err => console.error('Failed to save dependencies:', err));
            }}
            renderInput={(params) => <TextField {...params} label="Depends on" placeholder="Add dependency..." />}
            renderTags={(value, getTagProps) =>
              value.map((slug, index) => {
                const { key, ...rest } = getTagProps({ index });
                return <Chip key={key} label={slug} size="small" {...rest} />;
              })
            }
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Entity Format</InputLabel>
            <Select
              value={settingsEntityFormat}
              label="Entity Format"
              onChange={e => {
                const newFormat = e.target.value as 'text' | 'json';
                setSettingsEntityFormat(newFormat);
                api.patch(`/schemes/prompts/entity-types/${entityType.id}/`, {
                  entity_format: newFormat,
                }).then(() => onEntityTypeUpdated())
                  .catch(err => console.error('Failed to save entity format:', err));
              }}
            >
              <MenuItem value="text">Text</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Prompt Versions Section */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Prompt Versions
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenPromptModal('create')}
            size="small"
          >
            New Version
          </Button>
        </Box>
        {loadingVersions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : versions.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No prompt versions for this entity type.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '10%' }}>Version</TableCell>
                <TableCell sx={{ width: '10%' }}>Status</TableCell>
                <TableCell sx={{ width: '70%' }}>Notes</TableCell>
                <TableCell sx={{ width: '10%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map((version) => (
                <TableRow
                  key={version.id}
                  hover
                  onClick={() => handleOpenPromptModal(
                    version.status === 'draft' ? 'edit' : 'edit',
                    version.id
                  )}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {version.version}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(version.status, version.is_active)}
                      size="small"
                      color={getStatusColor(version.status, version.is_active)}
                      variant={version.is_active ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                      {version.notes || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, version)}
                    >
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Saved Entities Section - shown for all types */}
      <Paper>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Saved Entities
            </Typography>
            <Chip
              label={`${entities.length} entities`}
              size="small"
              variant="outlined"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenEntityDialog()}
            >
              Add Entity
            </Button>
          </Box>
        </Box>

        {entityError && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setEntityError(null)}>
            {entityError}
          </Alert>
        )}

        {loadingEntities ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : entities.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No saved entities for {entityType.label}.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenEntityDialog()}
              sx={{ mt: 1 }}
              size="small"
            >
              Add First Entity
            </Button>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {entityType.entity_format === 'json' ? (
                  <>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Metadata</TableCell>
                  </>
                ) : (
                  <TableCell sx={{ fontWeight: 600 }}>Content</TableCell>
                )}
                <TableCell width={100} sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell width={50} />
              </TableRow>
            </TableHead>
            <TableBody>
              {entities.map((entity) => (
                <TableRow
                  key={entity.id}
                  hover
                  onClick={() => handleOpenEntityDialog(entity)}
                  sx={{ cursor: 'pointer' }}
                >
                  {entityType.entity_format === 'json' ? (
                    <>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {entity.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 400,
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {JSON.stringify(entity.metadata)}
                        </Typography>
                      </TableCell>
                    </>
                  ) : (
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {entity.definition || entity.name}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip
                      label={entity.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={entity.is_active ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteEntityClick(entity);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Prompt Version Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        {selectedVersion && !selectedVersion.is_active && (
          <MenuItem onClick={handleReleaseClick}>
            <ListItemIcon>
              <ReleaseIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>{selectedVersion.status === 'archived' ? 'Re-activate' : 'Release'}</ListItemText>
          </MenuItem>
        )}
        {selectedVersion && !selectedVersion.is_active && (
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Delete Prompt Version Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Prompt Version</DialogTitle>
        <DialogContent>
          {deleteVersion && (
            <>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete <strong>{deleteVersion.entity_type_name} {deleteVersion.version}</strong>?
              </Typography>
              {feedbackCount > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This version has evaluation feedback on <strong>{feedbackCount} press release{feedbackCount !== 1 ? 's' : ''}</strong>.
                  Deleting this version will also delete all associated feedback.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                This action cannot be undone.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release Prompt Version Dialog */}
      <Dialog open={releaseDialogOpen} onClose={handleReleaseCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Release Prompt Version</DialogTitle>
        <DialogContent>
          {releaseVersion && (
            <>
              <Typography variant="body1" gutterBottom>
                Release <strong>{releaseVersion.entity_type_name} {releaseVersion.version}</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                This will make it the active version and run extractions against the eval set.
                Press releases that already have saved results will be skipped.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={forceOverwrite}
                    onChange={(e) => setForceOverwrite(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    Re-run all press releases (overwrite existing results)
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
              {forceOverwrite && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Existing extraction results for this entity type will be overwritten.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReleaseCancel} disabled={releasing}>
            Cancel
          </Button>
          <Button onClick={handleReleaseConfirm} color="success" variant="contained" disabled={releasing}>
            {releasing ? 'Releasing...' : 'Release'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Entity Dialog */}
      <Dialog open={entityDialogOpen} onClose={handleCloseEntityDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEntity ? 'Edit Entity' : 'Add New Entity'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {entityType.entity_format === 'json' ? (
              <>
                <TextField
                  fullWidth
                  label="Name"
                  value={entityFormData.name}
                  onChange={(e) => setEntityFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Centers for Medicare & Medicaid Services (CMS)"
                />
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="JSON Metadata"
                  value={entityFormData.metadata}
                  onChange={(e) => setEntityFormData(prev => ({ ...prev, metadata: e.target.value }))}
                  placeholder='{"key": "value"}'
                  helperText="Structured JSON object injected into prompts"
                  sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                />
              </>
            ) : (
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Content"
                value={entityFormData.definition}
                onChange={(e) => setEntityFormData(prev => ({ ...prev, definition: e.target.value }))}
                placeholder="Free-form text injected into prompts..."
                helperText="Each saved entity is injected as-is into the prompt"
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={entityFormData.is_active}
                  onChange={(e) => setEntityFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                />
              }
              label="Active (included in prompt injection)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEntityDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEntity}
            disabled={savingEntity || (entityType.entity_format === 'text' ? !entityFormData.definition.trim() : (!entityFormData.name.trim() || !entityFormData.metadata.trim()))}
          >
            {savingEntity ? 'Saving...' : (editingEntity ? 'Save Changes' : 'Add Entity')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Entity Confirmation Dialog */}
      <Dialog open={deleteEntityDialogOpen} onClose={() => setDeleteEntityDialogOpen(false)} maxWidth="sm">
        <DialogTitle>Delete Entity</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteEntity?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEntityDialogOpen(false)} disabled={deletingEntity}>
            Cancel
          </Button>
          <Button onClick={handleDeleteEntityConfirm} color="error" variant="contained" disabled={deletingEntity}>
            {deletingEntity ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Entity Type Confirmation Dialog */}
      <Dialog open={deleteTypeDialogOpen} onClose={() => setDeleteTypeDialogOpen(false)} maxWidth="sm">
        <DialogTitle>Delete Entity Type</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{entityType.short_name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will also delete all prompt versions ({versions.length}), saved entities ({entities.length}), test runs, and extraction results for this type.
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTypeDialogOpen(false)} disabled={deletingType}>
            Cancel
          </Button>
          <Button onClick={handleDeleteEntityType} color="error" variant="contained" disabled={deletingType}>
            {deletingType ? 'Deleting...' : 'Delete Entity Type'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prompt Editor Modal */}
      <Dialog
        open={promptModalOpen}
        onClose={handleClosePromptModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: '85vh', maxHeight: '85vh' } }}
      >
        <DialogTitle>
          {promptModalMode === 'create' ? 'New Prompt Version' : `Edit ${versions.find(v => v.id === promptModalVersionId)?.version || 'Draft'}`}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', gap: 2, pt: '8px !important', overflow: 'hidden' }}>
          {/* Left Panel — Prompt Editor (~55%) */}
          <Box sx={{ flex: '0 0 55%', display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto', pr: 1 }}>
            {promptError && (
              <Alert severity="error" onClose={() => setPromptError(null)}>
                {promptError}
              </Alert>
            )}

            {loadingPromptText ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {/* Available Variables — above the prompt textarea */}
                {(dependencyVars.length > 0 || builtinVars.length > 0) && (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {dependencyVars.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                            Dependency variables
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {dependencyVars.map(slug => (
                              <Chip
                                key={slug}
                                label={`{${slug}}`}
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => handleInsertVariable(slug)}
                                sx={{ fontFamily: 'monospace', fontSize: '0.8rem', cursor: 'pointer' }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          Built-in variables
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {builtinVars.map(v => (
                            <Chip
                              key={v}
                              label={`{${v}}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  label="Prompt Text"
                  value={promptText}
                  onChange={e => setPromptText(e.target.value)}
                  inputRef={promptTextRef}
                  placeholder="Enter your prompt template..."
                  sx={{
                    flex: 1,
                    display: 'flex',
                    '& .MuiInputBase-root': {
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      lineHeight: 1.5,
                      flex: 1,
                      alignItems: 'stretch',
                    },
                    '& .MuiInputBase-input': {
                      overflow: 'auto !important',
                    },
                  }}
                />

                {/* Model settings row */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Reasoning</InputLabel>
                    <Select
                      value={reasoningEffort}
                      label="Reasoning"
                      onChange={e => setReasoningEffort(e.target.value as ReasoningEffort)}
                    >
                      <MenuItem value="none">
                        <Box>
                          <Typography variant="body2">None</Typography>
                          <Typography variant="caption" color="text.secondary">Fastest — no internal thinking, best for simple classification</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="low">
                        <Box>
                          <Typography variant="body2">Low</Typography>
                          <Typography variant="caption" color="text.secondary">Quick thinking — good for straightforward extraction</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="medium">
                        <Box>
                          <Typography variant="body2">Medium</Typography>
                          <Typography variant="caption" color="text.secondary">Balanced — thinks through the problem before answering</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="high">
                        <Box>
                          <Typography variant="body2">High</Typography>
                          <Typography variant="caption" color="text.secondary">Slowest — explores multiple angles, best for complex analysis</Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={webSearchEnabled}
                        onChange={e => setWebSearchEnabled(e.target.checked)}
                        size="small"
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Web Search</Typography>
                        <Typography variant="caption" color={webSearchEnabled ? 'primary' : 'text.secondary'}>
                          {webSearchEnabled ? 'Model can search the web' : 'No web access'}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

                <TextField
                  size="small"
                  label="Version Notes"
                  value={versionNotes}
                  onChange={e => setVersionNotes(e.target.value)}
                  placeholder="What changed in this version?"
                  fullWidth
                />
              </>
            )}
          </Box>

          {/* Right Panel — Inline Test Runner (~45%) */}
          <Box sx={{
            flex: '0 0 45%',
            display: 'flex',
            flexDirection: 'column',
            borderLeft: '1px solid',
            borderColor: 'divider',
            pl: 2,
            overflow: 'hidden',
          }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Test Runner
            </Typography>

            {/* Press Release selector + Test button */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Press Release</InputLabel>
                <Select
                  value={selectedTestPrId}
                  label="Press Release"
                  onChange={e => setSelectedTestPrId(e.target.value as string)}
                >
                  {evalPressReleases.map(pr => (
                    <MenuItem key={pr.press_release_id} value={pr.press_release_id}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 350 }}>
                        {pr.case_name || pr.press_release_id}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="small"
                startIcon={inlineTestStatus === 'running' ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                onClick={handleInlineTest}
                disabled={inlineTestStatus === 'running' || !selectedTestPrId || !promptText.trim() || loadingPromptText}
                sx={{ whiteSpace: 'nowrap', minWidth: 80, height: 40 }}
              >
                {inlineTestStatus === 'running' ? 'Testing...' : 'Test'}
              </Button>
            </Box>

            {/* Press release preview */}
            {selectedTestPrId && (
              <Box sx={{ mb: 2 }}>
                <Button
                  size="small"
                  startIcon={<ArticleIcon sx={{ fontSize: 16 }} />}
                  endIcon={<ExpandMoreIcon sx={{
                    fontSize: 16,
                    transform: prPreviewOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }} />}
                  onClick={() => setPrPreviewOpen(!prPreviewOpen)}
                  sx={{ textTransform: 'none', color: 'text.secondary', mb: 0.5 }}
                >
                  {prPreviewOpen ? 'Hide' : 'View'} Press Release
                </Button>
                <Collapse in={prPreviewOpen}>
                  <Box sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    maxHeight: 300,
                    overflow: 'auto',
                  }}>
                    {loadingPrDetail ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={20} />
                      </Box>
                    ) : selectedPrDetail ? (
                      <Box sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                          {selectedPrDetail.title}
                        </Typography>
                        <Box
                          sx={{
                            fontSize: '0.8rem',
                            lineHeight: 1.6,
                            color: 'text.secondary',
                            '& p': { my: 0.5 },
                          }}
                          dangerouslySetInnerHTML={{ __html: selectedPrDetail.body }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2, fontStyle: 'italic' }}>
                        Could not load press release content.
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}

            {evalPressReleases.length === 0 && !loadingPromptText && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No eval press releases found. Add press releases to the eval set first.
              </Typography>
            )}

            {/* Test status */}
            {inlineTestStatus === 'running' && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress sx={{ mb: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Running extraction (this may take 1-2 minutes)...
                </Typography>
              </Box>
            )}

            {/* Test error */}
            {inlineTestError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInlineTestError(null)}>
                {inlineTestError}
              </Alert>
            )}

            {/* Test result */}
            {inlineTestResult && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip label="Completed" size="small" color="success" variant="outlined" />
                  <Typography variant="caption" color="text.secondary">
                    {inlineTestResult.latency_ms ? `${(inlineTestResult.latency_ms / 1000).toFixed(1)}s` : ''}
                    {inlineTestResult.tokens_used ? ` | ${inlineTestResult.tokens_used} tokens` : ''}
                  </Typography>
                </Box>
                <Box sx={{
                  flex: 1,
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                }}>
                  <pre style={{
                    margin: 0,
                    padding: '12px',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}>
                    {typeof inlineTestResult.output === 'string'
                      ? inlineTestResult.output
                      : JSON.stringify(inlineTestResult.output, null, 2)}
                  </pre>

                  {/* Chain of Thought / metadata */}
                  {inlineTestResult.metadata?.reasoning?.length > 0 && (
                    <Box sx={{ px: 1.5, pb: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                      <ChainOfThought steps={inlineTestResult.metadata.reasoning} defaultExpanded={false} />
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Idle state — no results yet */}
            {inlineTestStatus === 'idle' && !inlineTestResult && evalPressReleases.length > 0 && (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Select a press release and click Test to run the current prompt.
                  <br />
                  No save required — tests use the prompt text as-is.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePromptModal} disabled={savingPrompt}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePrompt}
            disabled={savingPrompt || loadingPromptText || !promptText.trim()}
            startIcon={savingPrompt ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {savingPrompt ? 'Saving...' : (promptModalMode === 'create' ? 'Create Draft' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test run progress notification */}
      <Snackbar
        open={!!activeTestRun}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ top: 80 }}
      >
        <Alert
          severity={activeTestRun?.status === 'failed' ? 'error' : activeTestRun?.status === 'completed' ? 'success' : 'info'}
          sx={{
            minWidth: 420,
            backgroundColor: 'rgba(30, 30, 30, 0.65)',
            backdropFilter: 'blur(12px)',
            color: 'white',
            '& .MuiAlert-message': { width: '100%' },
            '& .MuiAlert-icon': { color: 'rgba(255,255,255,0.9)' },
            '& .MuiTypography-root': { color: 'white' },
            '& .MuiLinearProgress-root': { backgroundColor: 'rgba(255,255,255,0.2)' },
            '& .MuiLinearProgress-bar': { backgroundColor: 'rgba(255,255,255,0.8)' },
          }}
          onClose={activeTestRun?.status === 'completed' || activeTestRun?.status === 'failed' ? () => setActiveTestRun(null) : undefined}
        >
          <Box>
            <Typography variant="subtitle2">
              {activeTestRun?.status === 'completed'
                ? `Released ${activeTestRun?.entityTypeLabel} ${activeTestRun?.version}`
                : activeTestRun?.status === 'failed'
                  ? `Failed to run extractions`
                  : `Releasing ${activeTestRun?.entityTypeLabel} ${activeTestRun?.version}...`}
            </Typography>
            {activeTestRun && activeTestRun.status !== 'completed' && activeTestRun.status !== 'failed' && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={(activeTestRun.completedPressReleases / activeTestRun.totalPressReleases) * 100}
                  sx={{ mb: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {activeTestRun.completedPressReleases} / {activeTestRun.totalPressReleases} extractions
                </Typography>
              </Box>
            )}
          </Box>
        </Alert>
      </Snackbar>
    </>
  );
}
